import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  Users, 
  Award,
  Calendar,
  BarChart3,
  Target,
  PlayCircle,
  FolderTree,
  ArrowRight
} from 'lucide-react';
import { LearningCollection } from '../types';

interface DashboardStats {
  totalUsers: number;
  totalTutorials: number;
  totalAssignments: number;
  completedAssignments: number;
  averageQuizScore: number;
  activeUsers: number;
  popularTutorials: Array<{
    id: string;
    title: string;
    completions: number;
  }>;
}

interface UserDashboard {
  assignedCourses: Array<{
    id: string;
    title: string;
    category: string;
    difficulty: string;
    dueDate?: string;
    progress: number;
    isCompleted: boolean;
    completedAt?: string;
  }>;
  completedCourses: Array<{
    id: string;
    title: string;
    category: string;
    completedAt: string;
    finalScore?: number;
  }>;
  inProgressCourses: Array<{
    id: string;
    title: string;
    category: string;
    currentStep: number;
    totalSteps: number;
    progress: number;
  }>;
  quizScores: Array<{
    tutorialId: string;
    tutorialTitle: string;
    bestScore: number;
    attempts: number;
    lastAttempt: string;
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [userDashboard, setUserDashboard] = useState<UserDashboard | null>(null);
  const [assignedCollections, setAssignedCollections] = useState<LearningCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.roles?.includes('admin');
  const isManager = user?.roles?.includes('manager');
  const isInstructor = user?.roles?.includes('instructor');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Authentication required - no token found');
        return;
      }


      // Fetch user dashboard data
      const userData = await apiService.request('/user-management/dashboard/user');
      setUserDashboard(userData.data);

      // Fetch assigned collections
      try {
        const collectionsResponse = await apiService.getAssignedCollections();
        if (collectionsResponse.success && collectionsResponse.data) {
          setAssignedCollections(collectionsResponse.data);
        }
      } catch (err) {
        console.error('Error fetching assigned collections:', err);
      }

      // Fetch admin stats if user is admin
      if (isAdmin) {
        const statsData = await apiService.request('/user-management/dashboard/stats');
        setStats(statsData.data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDifficultyColor = (difficulty: string | null | undefined) => {
    if (!difficulty) {
      return 'text-gray-600 bg-gray-100';
    }
    switch (difficulty.toLowerCase()) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-gray-600 mt-2">
            {isAdmin ? 'Admin Dashboard' : 'Your Learning Dashboard'}
          </p>
        </div>

        {/* Admin Statistics */}
        {isAdmin && stats && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Platform Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <BookOpen className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Tutorials</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.totalTutorials}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Target className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Assignments</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.totalAssignments}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Quiz Score</p>
                    <p className="text-2xl font-semibold text-gray-900">{Math.round(stats.averageQuizScore)}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Popular Tutorials */}
            {stats.popularTutorials.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Tutorials</h3>
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6">
                    <div className="space-y-4">
                      {stats.popularTutorials.map((tutorial, index) => (
                        <button
                          key={tutorial.id}
                          onClick={() => navigate(`/tutorials/${tutorial.id}`)}
                          className="w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center">
                            <span className="text-2xl font-bold text-blue-600 mr-4">#{index + 1}</span>
                            <div>
                              <p className="font-medium text-gray-900">{tutorial.title}</p>
                              <p className="text-sm text-gray-600">{tutorial.completions} completions</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Dashboard Content */}
        {userDashboard && (
          <div className="space-y-8">
            {/* My Learning Paths */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">My Learning Paths</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {assignedCollections.length > 0 
                      ? 'Collections assigned to you'
                      : 'No learning paths assigned yet. Contact your manager to get started.'}
                  </p>
                </div>
                {assignedCollections.length === 0 && (
                  <button
                    onClick={() => navigate('/collections')}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Browse Collections
                  </button>
                )}
              </div>
              {assignedCollections.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {assignedCollections.map((collection) => {
                    const progress = collection.progress;
                    const completionPercentage = progress?.completionPercentage || 0;
                    const completedTutorials = progress?.completedTutorials || 0;
                    const totalTutorials = progress?.totalTutorials || collection.totalTutorialCount || 0;

                    return (
                      <div
                        key={collection.id}
                        onClick={() => navigate(`/collections/${collection.id}`)}
                        className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-3 flex-1">
                            {collection.icon && (
                              <div className="text-3xl">{collection.icon}</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                {collection.name}
                              </h3>
                              {collection.description && (
                                <p className="text-sm text-gray-600 line-clamp-2">
                                  {collection.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Progress</span>
                            <span className="text-sm text-gray-600">
                              {completedTutorials} of {totalTutorials} tutorials ({Math.round(completionPercentage)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${completionPercentage}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center space-x-1">
                            <BookOpen className="w-3 h-3" />
                            <span>{totalTutorials} tutorials</span>
                          </span>
                          {collection.categoryCount && collection.categoryCount > 0 && (
                            <span className="flex items-center space-x-1">
                              <FolderTree className="w-3 h-3" />
                              <span>{collection.categoryCount} categories</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Learning Paths Assigned</h3>
                  <p className="text-gray-600 mb-6">
                    Learning paths are collections of tutorials organized for your development.
                    <br />
                    Contact your manager or administrator to get assigned to a learning path.
                  </p>
                  <button
                    onClick={() => navigate('/collections')}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Browse Available Collections
                  </button>
                </div>
              )}
            </div>

            {/* Assigned Courses */}
            {userDashboard.assignedCourses.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Assigned Courses</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {userDashboard.assignedCourses.map((course) => (
                    <div key={course.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{course.title || 'Untitled Course'}</h3>
                          <p className="text-sm text-gray-600">{course.category || 'Uncategorized'}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                          {course.difficulty || 'Unknown'}
                        </span>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Progress</span>
                          <span className="text-sm text-gray-600">{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {course.dueDate && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="h-4 w-4 mr-1" />
                              Due: {formatDate(course.dueDate)}
                            </div>
                          )}
                          {course.isCompleted && (
                            <div className="flex items-center text-green-600">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              <span className="text-sm font-medium">Completed</span>
                            </div>
                          )}
                        </div>
                        
                        {!course.isCompleted && (
                          <button
                            onClick={() => navigate(`/tutorials/${course.id}`)}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <PlayCircle className="h-4 w-4 mr-2" />
                            {course.progress > 0 ? 'Continue Course' : 'Start Course'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* In Progress Courses */}
            {userDashboard.inProgressCourses.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Continue Learning</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {userDashboard.inProgressCourses.map((course) => (
                    <div key={course.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{course.title || 'Untitled Course'}</h3>
                          <p className="text-sm text-gray-600">{course.category || 'Uncategorized'}</p>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <PlayCircle className="h-4 w-4 mr-1" />
                          Step {course.currentStep || 0} of {course.totalSteps || 0}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Progress</span>
                          <span className="text-sm text-gray-600">{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <button 
                        onClick={() => navigate(`/tutorials/${course.id}`)}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Continue Course
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Courses */}
            {userDashboard.completedCourses.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Completed Courses</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {userDashboard.completedCourses.map((course) => (
                    <div key={course.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{course.title || 'Untitled Course'}</h3>
                          <p className="text-sm text-gray-600">{course.category || 'Uncategorized'}</p>
                        </div>
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span className="text-sm font-medium">Completed</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Completed: {formatDate(course.completedAt)}</span>
                        {course.finalScore && (
                          <span className="font-medium">Score: {course.finalScore}%</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quiz Scores */}
            {userDashboard.quizScores.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quiz Performance</h2>
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6">
                    <div className="space-y-4">
                      {userDashboard.quizScores.map((quiz) => (
                        <div key={quiz.tutorialId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{quiz.tutorialTitle}</p>
                            <p className="text-sm text-gray-600">
                              {quiz.attempts} attempt{quiz.attempts !== 1 ? 's' : ''} • 
                              Last attempt: {formatDate(quiz.lastAttempt)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-600">{quiz.bestScore}%</p>
                            <p className="text-sm text-gray-600">Best Score</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {userDashboard.assignedCourses.length === 0 && 
             userDashboard.inProgressCourses.length === 0 && 
             userDashboard.completedCourses.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No courses yet</h3>
                <p className="text-gray-600 mb-6">
                  {isManager ? 'Start by assigning courses to users' : 'Browse the course catalog to get started'}
                </p>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                  Browse Courses
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
